import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "services/api";
import { __ } from "services/Translator";

export default function CountrySelect({
  selected = null,
  onChange,
  placeholder = __("Search country or code..."),
  className = "country-select",
  listMaxHeight = 280,
  variant = "country",
  position = "below",
  allowedCodes = null,
  resolveStringSelection = true,
  buttonClassName = "",
  panelClassName = "",
  searchInputClassName = "",
  optionClassName = "",
  showSearch = true,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const [countries, setCountries] = useState([]);
  const [busy, setBusy] = useState(false);

  const fetchCountries = async () => {
    try {
      setBusy(true);
      const res = await api.get("/countries");
      if (res.status === 200) setCountries(res.data || []);
    } catch (_) {
      // ignore
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  const selectedResolved = useMemo(() => {
    if (!selected) return null;
    if (typeof selected === "string") {
      const code = selected.toUpperCase();
      return countries.find((c) => (c.code || "").toUpperCase() === code) || null;
    }
    return selected;
  }, [selected, countries]);

  const allowedCodeSet = useMemo(() => {
    if (!Array.isArray(allowedCodes) || !allowedCodes.length) {
      return null;
    }
    return new Set(allowedCodes.map((code) => String(code || "").toUpperCase()).filter(Boolean));
  }, [allowedCodes]);

  const availableCountries = useMemo(() => {
    if (!allowedCodeSet) {
      return countries;
    }
    return countries.filter((c) => allowedCodeSet.has((c.code || "").toUpperCase()));
  }, [countries, allowedCodeSet]);

  useEffect(() => {
    if (!resolveStringSelection) return;
    if (!selected || typeof selected !== "string") return;
    if (!countries.length) return;

    const code = selected.toUpperCase();
    const found = availableCountries.find((c) => (c.code || "").toUpperCase() === code);
    if (found) {
      onChange?.(found);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, availableCountries, resolveStringSelection]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return availableCountries;
    return availableCountries.filter((c) => {
      const name = (c.name || "").toLowerCase();
      const nicename = (c.nicename || "").toLowerCase();
      const iso = (c.code || "").toLowerCase();
      const phonecode = String(c.phonecode || "").toLowerCase();
      return (
        name.includes(q) || nicename.includes(q) || iso.includes(q) || phonecode.includes(q) || ("+" + phonecode).includes(q)
      );
    });
  }, [availableCountries, query]);

  useEffect(() => {
    if (highlight > filtered.length - 1) {
      setHighlight(Math.max(0, filtered.length - 1));
    }
  }, [filtered.length, highlight]);

  useEffect(() => {
    function onDocClick(e) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    if (open && showSearch) requestAnimationFrame(() => inputRef.current?.focus());
  }, [open, showSearch]);

  useEffect(() => {
    if (!listRef.current) return;
    const item = listRef.current.querySelector(`[data-index="${highlight}"]`);
    if (!item) return;
    const { offsetTop, offsetHeight } = item;
    const { scrollTop, clientHeight } = listRef.current;
    if (offsetTop < scrollTop) listRef.current.scrollTop = offsetTop;
    else if (offsetTop + offsetHeight > scrollTop + clientHeight) {
      listRef.current.scrollTop = offsetTop - (clientHeight - offsetHeight);
    }
  }, [highlight]);

  const selectItem = (idx) => {
    const item = filtered[idx];
    if (!item) return;
    onChange?.(item);
    setOpen(false);
    setQuery("");
  };

  const onButtonKeyDown = (e) => {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(true);
      setHighlight(0);
    }
  };

  const onSearchKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      selectItem(highlight);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      setQuery("");
    }
  };

  const selectedLabel =
    variant === "country"
      ? selectedResolved
        ? `${selectedResolved.nicename || selectedResolved.code}`
        : __("Select country")
      : selectedResolved
        ? `+${selectedResolved.phonecode} (${selectedResolved.code})`
        : __("Select country");

  const getFlagSrc = (iso2) => `/flags/${iso2.toLowerCase()}.svg`;

  const panelStyle = {
    position: "absolute",
    zIndex: 1000,
    top: position === "over" ? "unset" : "100%",
    bottom: position === "over" ? "100%" : "unset",
    left: 0,
    right: 0,
    marginTop: position === "below" ? 6 : 0,
    marginBottom: position === "over" ? 6 : 0,
    background: "#1b1b1b",
    border: "1px solid #444",
    borderRadius: 8,
    boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
  };

  return (
    <div className={className} ref={rootRef} style={{ position: "relative", minWidth: 50 }}>
      <button
        type="button"
        className={`country-select__button ${buttonClassName}`.trim()}
        onClick={() => !busy && setOpen((o) => !o)}
        onKeyDown={onButtonKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          opacity: busy ? 0.6 : 1,
          cursor: busy ? "not-allowed" : "pointer",
        }}
      >
        {selectedResolved?.code ? (
          <img
            src={getFlagSrc(selectedResolved.code)}
            alt={selectedResolved.nicename || selectedResolved.code}
            style={{ width: 22, height: 16, objectFit: "cover", borderRadius: 2 }}
            onError={(e) => {
              e.currentTarget.style.visibility = "hidden";
            }}
          />
        ) : (
          <div style={{ width: 22, height: 16, background: "#555", borderRadius: 2 }} />
        )}
        <span>{selectedLabel}</span>
        <span style={{ marginLeft: "auto", opacity: 0.7 }}>▾</span>
      </button>

      {open && (
        <div className={`country-select__panel ${panelClassName}`.trim()} role="listbox" style={panelStyle}>
          {position === "below" && showSearch && (
            <div style={{ padding: 8 }}>
              <input
                ref={inputRef}
                type="text"
                className={`country-select__search ${searchInputClassName}`.trim()}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onSearchKeyDown}
                placeholder={placeholder}
              />
            </div>
          )}

          <div ref={listRef} style={{ maxHeight: listMaxHeight, overflowY: "auto" }}>
            {filtered.length === 0 && <div style={{ padding: 12, color: "#bbb" }}>{__("No matches")}</div>}

            {filtered.map((c, idx) => {
              const isActive = idx === highlight;
              return (
                <div
                  key={c.code || c.id || idx}
                  data-index={idx}
                  role="option"
                  aria-selected={selectedResolved?.code === c.code}
                  className={`country-select__option ${isActive ? "is-active" : ""} ${optionClassName}`.trim()}
                  onMouseEnter={() => setHighlight(idx)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => selectItem(idx)}
                >
                  <img
                    src={getFlagSrc(c.code)}
                    alt={c.nicename || c.code}
                    style={{ width: 22, height: 16, objectFit: "cover", borderRadius: 2 }}
                    onError={(e) => {
                      e.currentTarget.style.visibility = "hidden";
                    }}
                  />
                  <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                    <span style={{ color: "#ccc" }}>{c.code}</span>
                    {variant === "country" && (c.nicename || c.name) && (
                      <span style={{ color: "#aaa" }}>• {c.nicename || c.name}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {position === "over" && showSearch && (
            <div style={{ padding: 8 }}>
              <input
                ref={inputRef}
                type="text"
                className={`country-select__search ${searchInputClassName}`.trim()}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onSearchKeyDown}
                placeholder={placeholder}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
